import {
  Box,
  Button,
  Center,
  Container,
  Divider,
  Flex,
  SimpleGrid,
  Spacer,
  Text,
} from "@chakra-ui/react";
import InfiniteScroll from "react-infinite-scroller";
import { FiBookmark } from "react-icons/fi";
import { useInfiniteQuery, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { usePremium } from "../../../contexts/Premium";

import {
  getComics,
  getSingleReadingOrder,
} from "../../../services/readingOrdersApi";
import Spinner from "../../../shared/components/Spinner";
import useStartReadingOrder from "../../../shared/hooks/useStartReadingOrder";
import ReadingOrderEndItem from "../../reading/components/ReadingOrderEndItem";
import ReadingOrderHeader from "../../reading/components/ReadingOrderHeader";
import AuthorFooter from "../components/AuthorFooter";
import SuggestCorrection from "../../../shared/components/SuggestCorrection";
import { useAuth } from "../../../contexts/Auth";

const BrowseOrderPreview = () => {
  let comics = [];
  const { readingOrderId } = useParams();
  const { isPremium } = usePremium();
  const { authData } = useAuth();
  const navigate = useNavigate();
  const { isLoading, startReadingOrder } = useStartReadingOrder();
  const readingOrderQuery = useQuery(["readingOrder", readingOrderId], () => {
    return getSingleReadingOrder(readingOrderId);
  });
  const comicsQuery = useInfiniteQuery(
    ["orderComics", readingOrderId],
    ({ pageParam = 1 }) => getComics(readingOrderId, pageParam),
    {
      keepPreviousData: true,
      getNextPageParam: (pageParam, allPages) => {
        if (pageParam?.meta.currentPage === pageParam?.meta.lastPage) {
          return undefined;
        }
        return pageParam?.meta?.currentPage + 1;
      },
    }
  );

  const uniqBy = (theArray) => {
    var seen = {};
    return theArray.filter((item) => {
      var k = `${item.id}-${item?.story}`;
      return seen.hasOwnProperty(k) ? false : (seen[k] = true);
    });
  };

  if (readingOrderQuery.isLoading) {
    return <Spinner />;
  }

  const readingOrder = readingOrderQuery.data.data;

  if (comicsQuery.data) {
    comics = uniqBy(
      [...comicsQuery?.data?.pages?.flatMap((group, i) => group.data)].sort(
        (a, b) => a.order - b.order
      )
    );
  }

  return (
    <Container maxW="container.lg">
      <SimpleGrid mt="6" columns={{ sm: 1, md: 1, lg: 2 }} spacing={2}>
        <Box>
          <Box bg="gray.700" borderRadius="lg">
            <ReadingOrderHeader
              totalComics={readingOrder.comicsCount}
              readingOrder={readingOrder}
              callToAction={
                <Button
                  disabled={isLoading}
                  colorScheme="red"
                  textColor="white"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    startReadingOrder(readingOrder.id, readingOrder.franchise);
                  }}
                >
                  Start reading
                </Button>
              }
              hideProgress
            />

            {!isPremium && (
              <Box mx="6" mt="4" px="1">
                <Flex align="flex-start">
                  <Box pt="1" color="gray.400">
                    <FiBookmark size="22" />
                  </Box>
                  <Text ml="3" fontSize="sm" color="gray.400">
                    <Text as="span" color="gray.200" fontWeight="semibold">
                      Preview
                    </Text>{" "}
                    · Track 50 issues free ·<br />
                    Go Premium to track all{" "}
                    {readingOrder.comicsCount?.toLocaleString()} comics.
                  </Text>
                </Flex>
              </Box>
            )}

            {!!readingOrder.description && (
              <Box pt="6" px="6" pb="6">
                {/* Header title / premium notice */}
                {readingOrder.description}
              </Box>
            )}

            {!!readingOrder?.description &&
              !!readingOrder.author &&
              !!readingOrder?.privacyMode && (
                <Box px="6">
                  <Divider />
                </Box>
              )}

            {!!readingOrder?.author && !!readingOrder?.privacyMode && (
              <AuthorFooter readingOrder={readingOrder} />
            )}
          </Box>

          {authData.id !== readingOrder?.author?.id && (
            <SuggestCorrection readingOrderId={readingOrder.id} />
          )}
        </Box>
        <Box bg="gray.700" borderRadius="lg">
          <Flex
            py="3"
            px="3"
            borderBottomColor="gray.900"
            borderBottomWidth="1px"
          >
            <Text fontSize="sm" color="gray.400" mt="2" ml="1">
              READING ORDER
            </Text>
            <Spacer />
          </Flex>
          {/* Reading order list */}
          {comicsQuery.isLoading ? (
            <Spinner />
          ) : (
            <div
              className="List"
              style={{
                width: `100%`,
                height: `75vh`,
                overflow: "auto",
              }}
            >
              <InfiniteScroll
                pageStart={1}
                loadMore={() => {
                  if (!comicsQuery.isFetchingNextPage) {
                    comicsQuery.fetchNextPage();
                  }
                }}
                height="75vh"
                useWindow={false}
                hasMore={comicsQuery.hasNextPage}
                endMessage={
                  <ReadingOrderEndItem>
                    This concludes our reading order, 'nuff said!
                  </ReadingOrderEndItem>
                }
                loader={
                  <Center>
                    <Button size="sm" variant="outline" w="96%">
                      Loading more issues...
                    </Button>
                  </Center>
                }
              >
                {comics.map((comic, index) => (
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    py="3"
                    borderBottomWidth="1px"
                    borderBottomColor="gray.900"
                    borderLeftWidth={"5px"}
                    borderLeftColor={"transparent"}
                  >
                    <Box px="3">{comic.order}.</Box>
                    <Box flexGrow={2}>
                      <Text
                        fontSize="0.9em"
                        lineHeight="1.25em"
                        overflowWrap="break-word"
                        wordBreak="break-word"
                        cursor="pointer"
                        onClick={() =>
                          navigate(`/comics/${comic.id}/view`, {
                            state: {
                              comic: comic,
                              previewOnly: true,
                            },
                          })
                        }
                      >
                        {comic.title} {comic.story && `[${comic.story}]`}
                      </Text>
                    </Box>
                  </Flex>
                ))}
              </InfiniteScroll>
            </div>
          )}
        </Box>
      </SimpleGrid>
    </Container>
  );
};

export default BrowseOrderPreview;
